/**
 * Formatting for the plain `YYYY-MM-DD` days the API deals in.
 *
 * Three screens had grown their own copy of this, and each copy was a
 * slightly different answer to the same two questions:
 *
 * 1. **Where is midnight?** `new Date("2026-01-01")` is midnight *UTC*, so
 *    everyone west of it reads the day before — a frame taken on the 1st is
 *    labelled the 31st in New York. Appending the time with no zone parses as
 *    local midnight, which is the day the person actually lived.
 * 2. **What does an unparseable day do?** Blanking hides a server bug;
 *    "Invalid Date" shows the reader our internals. Falling back to the raw
 *    value keeps the page honest and keeps the defect visible.
 *
 * Constructing an `Intl.DateTimeFormat` is the expensive half of formatting a
 * date, so the formatters are kept per locale and shape. A feed of cards would
 * otherwise build one per card, per render.
 */

const formats = new Map<string, Intl.DateTimeFormat>();

function formatter(shape: string, locale: string, options: Intl.DateTimeFormatOptions) {
  const key = `${shape}:${locale}`;
  let format = formats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    formats.set(key, format);
  }
  return format;
}

function parseDay(day: string): Date | null {
  const parsed = new Date(`${day}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * The year's marker is not a word, and it does not begin a line.
 *
 * `Intl` writes a Russian date as "25 сентября 2026 г." with three ordinary
 * spaces, and the last of them is one the browser will happily break at.
 * It does: the day panel's heading, which shares its line with a Close
 * button, came out as "25 сентября 2026" over a line holding nothing but
 * "г." — seen in the harness's pictures on the 26th. Russian typography
 * binds that marker to the year the way a unit is bound to its number: it
 * is an abbreviation of "года", not a word of its own, and a line that
 * starts with it reads as a mistake.
 *
 * So the space before it is made non-breaking, and nothing else is touched:
 * the two other languages this app speaks put no particle after the year,
 * and their dates come back exactly as `Intl` wrote them. The day, the
 * month and the year still wrap between themselves, which is what makes a
 * long date fit a narrow column at all.
 */
function bindYearMarker(formatted: string): string {
  return formatted.replace(/ (г\.)$/u, "\u00a0$1");
}

/** The whole day a person would say aloud: "January 1, 2026". */
export function formatDay(day: string, locale: string): string {
  const parsed = parseDay(day);
  if (!parsed) return day;
  return bindYearMarker(
    formatter("day", locale, { day: "numeric", month: "long", year: "numeric" }).format(parsed),
  );
}

/** Day and month, the way "Jul 18" reads — for lists, where the year is noise. */
export function formatShortDay(day: string, locale: string): string {
  const parsed = parseDay(day);
  if (!parsed) return day;
  return formatter("shortDay", locale, { month: "short", day: "numeric" }).format(parsed);
}

/**
 * Abbreviated month, with the year: "Aug 1, 2026".
 *
 * For places where the long form does not fit — the timelapse scrubber sits
 * in a row beside the slider, and a date that grows from "May" to "September"
 * would resize the control being dragged.
 */
export function formatMediumDay(day: string, locale: string): string {
  const parsed = parseDay(day);
  if (!parsed) return day;
  return bindYearMarker(
    formatter("mediumDay", locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(parsed),
  );
}

/**
 * The local calendar day an instant fell on — `2026-09-22` for
 * `2026-09-21T23:30:00Z` read at GMT+8.
 *
 * The API's timestamps are instants in UTC, and taking the first ten
 * characters of one names the day in Greenwich, not the day the person
 * was in: a reply sent at eight in the evening in Los Angeles was dated
 * tomorrow, and an account made at seven in the morning here was "on
 * Selfeed since" yesterday. The instant is parsed — a full timestamp with
 * its zone parses as itself — and read back through local getters, the
 * way `todayIso` names today. An unparseable value keeps its raw slice,
 * the same honesty as the formatters above.
 */
export function localDayOf(timestamp: string): string {
  // A day with no time of day is already a day, and parsing it would make
  // it the instant of midnight UTC — the shift this function exists to undo.
  if (!timestamp.includes("T")) return timestamp.slice(0, 10);
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return timestamp.slice(0, 10);
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${parsed.getFullYear()}-${month}-${day}`;
}
